/* eslint-disable no-param-reassign */
/**
 * Utility functions for ag-Grid number formatting with i18n support
 */

export interface NumberFormatOptions {
  minimumFractionDigits?: number
  maximumFractionDigits?: number
  style?: 'decimal' | 'currency' | 'percent'
  currency?: string
}

/**
 * Formats a number according to locale settings
 */
export const formatNumber = (
  value: number | string | null | undefined,
  locale: string,
  options: NumberFormatOptions = {}
): string => {
  if (value === null || value === undefined || value === '') {
    return ''
  }

  const numValue = typeof value === 'string' ? parseFloat(value) : value

  if (Number.isNaN(numValue)) {
    return value?.toString() || ''
  }

  const defaultOptions: NumberFormatOptions = {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    style: 'decimal',
    ...options
  }

  try {
    return new Intl.NumberFormat(locale, defaultOptions).format(numValue)
  } catch (error) {
    // Fallback jos locale ei toimi
    return numValue.toFixed(defaultOptions.maximumFractionDigits || 2)
  }
}

/**
 * Creates a valueFormatter function for ag-Grid with proper locale formatting
 */
export const createGridValueFormatter = (locale: string, options: NumberFormatOptions = {}) => {
  return (params: any) => {
    return formatNumber(params.value, locale, options)
  }
}

/**
 * Gets locale-specific number formatting settings
 */
export const getLocaleSettings = (locale: string) => {
  const language = locale.split('-')[0]

  const settings = {
    fi: { thousandSeparator: ' ', decimalSeparator: ',' },
    en: { thousandSeparator: ',', decimalSeparator: '.' },
    sv: { thousandSeparator: ' ', decimalSeparator: ',' }
  }

  return settings[language as keyof typeof settings] || settings.fi
}

/**
 * Parses a localized number string to a JavaScript number
 */
export const parseLocalizedNumber = (value: string | number | null | undefined, locale: string): number | null => {
  // Jos value on jo number, palauta se suoraan
  if (typeof value === 'number') {
    return Number.isNaN(value) ? null : value
  }

  // Jos value on string, jatka entiseen tapaan
  if (!value || value.toString().trim() === '') {
    return null
  }

  const cleanValue = value.toString().trim()

  // Määrittele locale-kohtaiset erottimet
  const localeSettings = getLocaleSettings(locale)

  try {
    // Poista tuhanserottimet ensin
    let normalized = cleanValue.replace(new RegExp(`\\${localeSettings.thousandSeparator}`, 'g'), '')

    // Vaihda desimaalierottimen pisteeksi jos se on pilkku
    if (localeSettings.decimalSeparator === ',') {
      // Tarkista onko useampi pilkku - jos on, viimeinen on desimaalierottain
      const commaCount = (normalized.match(/,/g) || []).length
      if (commaCount === 1) {
        normalized = normalized.replace(',', '.')
      } else if (commaCount > 1) {
        // Useampi pilkku - viimeinen on desimaalierottain
        const lastCommaIndex = normalized.lastIndexOf(',')
        normalized = `${normalized.substring(0, lastCommaIndex).replace(/,/g, '')}.${normalized.substring(
          lastCommaIndex + 1
        )}`
      }
    }

    const parsed = parseFloat(normalized)
    return Number.isNaN(parsed) ? null : parsed
  } catch (error) {
    return null
  }
}

/**
 * Creates a valueSetter function for ag-Grid with proper locale parsing
 */
export const createGridValueSetter = (locale: string) => {
  return (params: any) => {
    const { newValue, colDef } = params

    if (newValue === null || newValue === '' || newValue === undefined) {
      params.data[colDef.field!] = null
      return true
    }

    const parsed = parseLocalizedNumber(newValue, locale)
    if (parsed === null) {
      return false // Virheellinen syöte
    }

    params.data[colDef.field!] = parsed
    return true
  }
}

/**
 * Utility to get locale from common Redux selectors
 */
export const getLocaleFromLanguage = (language: string): string => {
  const localeMap: Record<string, string> = {
    fi: 'fi-FI',
    en: 'en-US',
    sv: 'sv-SE'
  }
  return localeMap[language] || 'fi-FI'
}
